import { useCallback, useRef, useState } from 'react'
import type { CardTextField, CardTextValue } from '@lunary/ui-web/card-text'
import { ConfirmDialog } from './confirm-dialog'
import styles from './confirm-dialog.module.css'

export type PublishCopy = {
  readonly title: string
  readonly text: string
  readonly confirmLabel: string
  readonly facts?: readonly (readonly [string, string])[]
}

export class PublishCancelled extends Error {
  constructor() {
    super('Публикация отменена')
  }
}

export function changedFields<K extends string>(fields: readonly CardTextField<K>[], from: CardTextValue<K>, to: CardTextValue<K>): string {
  return fields
    .filter((f) => from[f.key].trim() !== to[f.key].trim())
    .map((f) => f.title ?? f.placeholder)
    .join(', ')
}

export function usePublishConfirm() {
  const [copy, setCopy] = useState<PublishCopy | null>(null)
  const [open, setOpen] = useState(false)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const ask = useCallback((next: PublishCopy) => {
    resolver.current?.(false)
    setCopy(next)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const confirmed = useCallback(
    async (next: PublishCopy) => {
      if (!(await ask(next))) throw new PublishCancelled()
    },
    [ask],
  )

  const finish = (ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setOpen(false)
  }

  const dialog = copy && (
    <ConfirmDialog
      open={open}
      title={copy.title}
      text={copy.text}
      confirmLabel={copy.confirmLabel}
      details={
        copy.facts && copy.facts.length > 0 ? (
          <dl className={styles.details}>
            {copy.facts.map(([label, value]) => (
              <div key={label}>
                <dt>{label}:</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : undefined
      }
      onConfirm={() => finish(true)}
      onClose={() => finish(false)}
    />
  )

  return { ask, confirmed, dialog }
}
